import { useSearchParams } from "react-router-dom";
import { FacetFilter } from "../../../../dto/FacetFilter";
import { useBranchLimits } from "@tlc/ls2pac/components/BranchLimits";
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { LocationSummary } from "@tlc/ls2pac/components/LocationDialog";
import { useLSSearch } from "./LSSearch";
import TuneIcon from "@mui/icons-material/Tune";
import { LSSearchFilters } from "./LSSearchFilters";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import { FILTER_DRAWER_WIDTH, useShowFilterSidebar } from "../LSSearchResults";
import { useSkipLinks } from "../../../../hooks/useSkipLinks";

export const LSResultsAppliedFilters = () => {
  const [_, setSearchParams] = useSearchParams();
  const { branchLimits, selectedBranchIds, updateSelectedBranches } =
    useBranchLimits();
  const {
    searchParams: { facetFilters, audience, branchFilters, hasFilters },
    clearAllFilters,
    clearAudienceFilter,
    clearFacetFilter,
  } = useLSSearch();
  const { translate } = useLanguage();
  const showFilterSidebar = useShowFilterSidebar();
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);
  const filterButtonRef = useRef<HTMLButtonElement>(null);

  const handleDeleteFacet = (facet: FacetFilter) => {
    clearFacetFilter(facet);
  };
  const handleDeleteLocations = () => {
    // TODO move to LSSearch or BranchLimits
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("branchFilters", JSON.stringify([])); // need to explicitly set to [] to clear because of context fallback
      return newParams;
    });
  };

  useEffect(() => {
    if (showFilterSidebar) {
      setFiltersOpen(false);
    }
  }, [showFilterSidebar]);

  const skipLinks = useMemo(() => {
    return !showFilterSidebar && !hasFilters
      ? [
          {
            label: translate("SkipToFilters"),
            targetRef: filterButtonRef,
            priority: 16,
          },
        ]
      : [];
  }, [showFilterSidebar, translate]);

  useSkipLinks(skipLinks);

  // TODO it may be better to extract the filter drawer to a different component and collate with the results layout
  return (
    <>
      <Drawer open={filtersOpen} onClose={() => setFiltersOpen(false)}>
        <Box width={FILTER_DRAWER_WIDTH} height="100vh">
          <Box flexGrow={1} sx={{ overflowY: "auto" }}>
            <LSSearchFilters />
          </Box>
          <Box
            sx={{
              backgroundColor: "background.paper",
              position: "sticky",
              bottom: 0,
            }}
          >
            <Divider />
            <Box display={"flex"} gap={1} padding={2}>
              <Button
                sx={{ flexGrow: 1 }}
                variant="contained"
                onClick={() => setFiltersOpen(false)}
              >
                {translate("done")}
              </Button>
              <Button
                sx={{ flexGrow: 1 }}
                variant="outlined"
                onClick={() => clearAllFilters()}
              >
                {translate("clear")}
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>
      <Box
        gap={1}
        flex={1}
        sx={{ display: "flex" }}
        alignItems={"center"}
        flexWrap={"wrap"}
      >
        {!showFilterSidebar && !hasFilters && (
          <Button
            ref={filterButtonRef}
            color="primary"
            startIcon={<TuneIcon />}
            onClick={() => setFiltersOpen(true)}
          >
            {translate("filter")}
          </Button>
        )}
        {showFilterSidebar && hasFilters && (
          <Typography sx={{ whiteSpace: "nowrap" }}>
            {translate("filteredBy")}
          </Typography>
        )}
        {!showFilterSidebar && hasFilters && (
          <IconButton color="primary" onClick={() => setFiltersOpen(true)}>
            <TuneIcon />
          </IconButton>
        )}
        {branchFilters?.length > 0 && (
          <Chip
            label={
              <LocationSummary
                branchLimits={branchLimits}
                selected={selectedBranchIds}
              ></LocationSummary>
            }
            variant="outlined"
            color="secondary"
            onDelete={handleDeleteLocations}
          />
        )}
        {audience && (
          <Chip
            label={audience}
            variant="outlined"
            color="secondary"
            onDelete={() => {
              clearAudienceFilter();
            }}
          />
        )}
        {facetFilters &&
          facetFilters.map((ff) => (
            <Chip
              key={`${ff.facetName}-${ff.facetValue}`}
              label={ff.facetDisplay}
              variant="outlined"
              color="secondary"
              onDelete={() => handleDeleteFacet(ff)}
            />
          ))}
        {hasFilters && (
          <Button onClick={() => clearAllFilters()}>
            {translate("clear")}
          </Button>
        )}
      </Box>
    </>
  );
};
